import { useEffect, useMemo, useState } from 'react';

import { getTradeInBonusForPhoneOnly } from '@services/products/plan';

import { MobilePlanType } from '@app-types/plan';

import { getQueryParam } from '@helpers/query-params';

import { MarketingType } from '@constants/plan';
import { TradeInStep } from '@constants/trade-in';

import { clearTradeInProduct, getTradeInLastStep, setTradeInBonus } from '@product-detail/helpers';
import { useQuery } from '@tanstack/react-query';

interface UseTradeInProps {
    selectedProductGencode: string;
    parentProductGencode: string;
    selectedPlan?: MobilePlanType | null;
}

function extractTradeInBonusFromPlan(plan: MobilePlanType | null | undefined, phoneGencode: string): number {
    const discounts = plan?.simulatedCart?.quotes
        ?.flatMap((quote) => quote.products)
        .find((product) => product.gencode === phoneGencode)?.details?.discounts;

    return discounts?.find((d) => d.marketingTypes.includes(MarketingType.TRADE_IN_BONUS))?.value ?? 0;
}

export function useTradeIn({ selectedProductGencode, parentProductGencode, selectedPlan }: UseTradeInProps) {
    const [tradeInStep, setTradeInStep] = useState<TradeInStep>(TradeInStep.NOT_STARTED);
    const [hasTradeInModalOpen, setHasTradeInModalOpen] = useState<boolean>(false);

    const noPlanTradeInBonusQuery = useQuery({
        queryKey: ['trade-in-bonus-no-plan', parentProductGencode],
        queryFn: () => getTradeInBonusForPhoneOnly(parentProductGencode),
        enabled: selectedPlan === null,
    });

    const tradeInBonus = useMemo(() => {
        if (selectedPlan !== null) {
            return extractTradeInBonusFromPlan(selectedPlan, selectedProductGencode);
        }
        return noPlanTradeInBonusQuery.data ?? 0;
    }, [selectedPlan, selectedProductGencode, noPlanTradeInBonusQuery.data]);

    const hasTradeInBonus = tradeInBonus > 0;
    const hasTradeIn = ![TradeInStep.NOT_STARTED, TradeInStep.SEARCH].includes(tradeInStep);

    const handleOpenTradeInModal = () => {
        if (tradeInStep === TradeInStep.CONFIRMED) {
            setTradeInStep(TradeInStep.CONFIRMATION);
        } else if (tradeInStep === TradeInStep.NOT_STARTED) {
            setTradeInStep(TradeInStep.SEARCH);
        }
        setHasTradeInModalOpen(true);
    };

    const resetTradeIn = () => {
        clearTradeInProduct();
        setTradeInStep(TradeInStep.NOT_STARTED);
        setHasTradeInModalOpen(false);
    };

    useEffect(() => {
        const step = getTradeInLastStep();
        if (getQueryParam('recovery') === 'true') {
            setHasTradeInModalOpen(true);
            setTradeInStep(step === TradeInStep.CONFIRMED ? TradeInStep.CONFIRMATION : step);
        } else {
            setTradeInStep(step);
        }
    }, []);

    useEffect(() => {
        setTradeInBonus(tradeInBonus > 0 ? tradeInBonus : undefined);
    }, [tradeInBonus, tradeInStep]); // tradeInStep is here to reset the bonus if user go back to step 1 or to post a new trade-in when confirmed

    return {
        tradeInStep,
        setTradeInStep,
        hasTradeIn,
        hasTradeInBonus,
        tradeInBonus,
        hasTradeInModalOpen,
        setHasTradeInModalOpen,
        handleOpenTradeInModal,
        resetTradeIn,
    };
}
